import { Box, Menu, MenuItem } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { GridCellParams, GridRowSelectionModel } from "@mui/x-data-grid";
import { t } from "i18next";
import type { FC, KeyboardEvent, MouseEvent, ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { useWelcomeReset } from "../../pages/Profile/hooks";
import useNurseFeatureData, { DEACTIVATE_POST_SHIFTS_CHECKBOX_ID } from "../../pages/Profile/v2/GeneralDetails/useNurseFeatureData";
import { useActivateNurse, useClearEmployeeStatus, useScheduleNurseDeactivation } from "../../pages/Profile/v2/hooks";
import { teamTableSortingKey } from "../../services/LocalStorageService";
import { GRID_ROW_HEIGHT, MOTION, NEUTRAL } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { formatPersonFullName, isNurseActiveInDepartment, isNurseOnLeaveOrSuspended } from "../../utils/formatters";
import type { DayOff, Nurse } from "../../utils/models";
import { daysOffAndPtoToNursePreferences } from "../../utils/utilFunctions";
import AddDayOffDialog from "../AddDayOffDialog/AddDayOffDialog";
import { SetPasswordDialog } from "../SetPasswordDialog/SetPasswordDialog";
import SystemDialog, { type DialogCheckedState } from "../UIToolkit/Popups/Dialog";
import { MenuItemWithReason } from "../UIToolkit/Popups/MenuItemWithReason";
import { SystemDataGrid } from "../UIToolkit/Tables/SystemDataGrid";
import EditEmployeeStatusDialog from "./EditEmployeeStatusDialog";
import { StatusDecisionDialog, useTrackStatusDecisionOpened } from "./statusDecisions/StatusDecisionDialog";
import type { OpenStatusDecision } from "./statusDecisions/types";
import { TEAM_DEFAULT_COLUMN_VISIBILITY } from "./teamColumnVisibility";
import { TEAM_ROW_ACTION_CLASS, useTeamGridColumns } from "./teamGridColumns";

type TeamTableProps = {
	nurses: Nurse[];
	/** Rendered inside the grid's leading toolbar slot — see SystemDataGrid's `leadingToolbarNodes`. */
	filtersNode?: ReactNode;
	/** Rendered in place of the default "No rows" message when the grid is empty. */
	emptyState?: ReactNode;
	/** Open HRIS status decisions keyed by nurse id — drives the per-row "!" badge. */
	decisionsByNurseId?: Map<number, OpenStatusDecision>;
	/** Loading the roster — renders skeleton rows in place of the grid body. */
	loading?: boolean;
	/** Column fields that have a matching filter-rail group — drives the column menu's "Filter by…". */
	canFilterByColumn?: (field: string) => boolean;
	onFilterByColumn?: (field: string) => void;
	filterByHint?: (field: string) => string | undefined;
	rowSelectionModel?: GridRowSelectionModel;
	onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
};

export const TeamTable: FC<TeamTableProps> = ({
	nurses,
	filtersNode,
	emptyState,
	decisionsByNurseId,
	loading = false,
	canFilterByColumn,
	onFilterByColumn,
	filterByHint,
	rowSelectionModel,
	onRowSelectionModelChange,
}) => {
	const { showSnackbar } = useSnackbar();
	const { userInfo } = useAuth();
	const { track } = useAnalytics();
	const navigate = useNavigate();
	const { sendWelcomeReset, isLoading: isResetPasswordLoading } = useWelcomeReset();
	const { scheduleDeactivation } = useScheduleNurseDeactivation();
	const { activateNurse } = useActivateNurse();
	const { clearEmployeeStatus, isClearingEmployeeStatus } = useClearEmployeeStatus();

	const [editStatusNurse, setEditStatusNurse] = useState<Nurse | null>(null);
	const [returnToActiveNurse, setReturnToActiveNurse] = useState<Nurse | null>(null);
	const [addPtoNurseId, setAddPtoNurseId] = useState<number | null>(null);
	const [deactivateDialogNurse, setDeactivateDialogNurse] = useState<Nurse | null>(null);
	const [activateDialogNurse, setActivateDialogNurse] = useState<Nurse | null>(null);
	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedNurse, setSelectedNurse] = useState<Nurse | null>(null);
	const [setPasswordNurse, setSetPasswordNurse] = useState<Nurse | null>(null);
	const [decisionDialog, setDecisionDialog] = useState<OpenStatusDecision | null>(null);
	const { chosenDepartment, refreshNursesData, updateNurse, isHospitalView } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	// Only hospital demos (Hospital type + inhouse2).
	const isRealHospitalDemo = isHospitalView;
	const trackDecisionOpened = useTrackStatusDecisionOpened();

	const handleDecisionClick = useCallback(
		(decision: OpenStatusDecision) => {
			trackDecisionOpened(decision, chosenDepartment.department_id);
			setDecisionDialog(decision);
		},
		[trackDecisionOpened, chosenDepartment.department_id],
	);

	const decisionDialogNurseName = useMemo(() => {
		if (!decisionDialog) return undefined;
		const nurse = nurses.find((n) => n.id === decisionDialog.nurseId);
		return nurse ? formatPersonName(nurse) : undefined;
	}, [decisionDialog, nurses, formatPersonName]);

	const handleResetPassword = useCallback(
		async (nurse: Nurse): Promise<void> => {
			const result = await sendWelcomeReset({
				isReset: true,
				nurseId: nurse.id,
				shouldCreateUser: false,
				departmentId: chosenDepartment.department_id,
				emailOrPhoneNumber: nurse.email || nurse.phoneNumber,
			});
			if (result) showSnackbar("Password reset link sent successfully", "success");
		},
		[sendWelcomeReset, chosenDepartment.department_id, showSnackbar],
	);

	const handleAddPto = useCallback((nurseId: number) => {
		setAddPtoNurseId(nurseId);
	}, []);

	const handleDeactivate = useCallback((nurse: Nurse) => {
		setDeactivateDialogNurse(nurse);
	}, []);

	const handleActivate = useCallback((nurse: Nurse) => {
		setActivateDialogNurse(nurse);
	}, []);

	const handleMenuClick = useCallback((event: MouseEvent<HTMLButtonElement>, nurse: Nurse) => {
		event.stopPropagation();
		setMenuAnchorEl(event.currentTarget);
		setSelectedNurse(nurse);
	}, []);

	const handleMenuClose = useCallback(() => {
		setMenuAnchorEl(null);
		setSelectedNurse(null);
	}, []);

	const handleCreateUserClick = useCallback(async () => {
		if (!selectedNurse) return;
		const nurse = selectedNurse;
		handleMenuClose();
		try {
			const result = await sendWelcomeReset({
				isReset: false,
				nurseId: nurse.id,
				shouldCreateUser: true,
				departmentId: chosenDepartment.department_id,
			});
			if (result) {
				showSnackbar("User created and welcome message sent", "success");
				await refreshNursesData(chosenDepartment.department_id);
			}
		} catch {
			showSnackbar("Cannot create user — employee is missing contact details (phone number or email)", "error");
		}
	}, [selectedNurse, handleMenuClose, sendWelcomeReset, chosenDepartment.department_id, showSnackbar, refreshNursesData]);

	const handleResetPasswordClick = useCallback(() => {
		if (selectedNurse) {
			handleMenuClose();
			handleResetPassword(selectedNurse);
		}
	}, [selectedNurse, handleMenuClose, handleResetPassword]);

	const handleAddPtoClick = useCallback(() => {
		if (selectedNurse) {
			handleMenuClose();
			handleAddPto(selectedNurse.id);
		}
	}, [selectedNurse, handleMenuClose, handleAddPto]);

	const handleEditStatusClick = useCallback(() => {
		if (selectedNurse) {
			handleMenuClose();
			setEditStatusNurse(selectedNurse);
		}
	}, [selectedNurse, handleMenuClose]);

	const handleReturnToActiveClick = useCallback(() => {
		if (!selectedNurse) return;
		const nurse = selectedNurse;
		handleMenuClose();
		setReturnToActiveNurse(nurse);
	}, [selectedNurse, handleMenuClose]);

	const handleReturnToActiveConfirm = useCallback(async () => {
		if (!returnToActiveNurse) return;
		const nurse = returnToActiveNurse;
		setReturnToActiveNurse(null);
		await clearEmployeeStatus({ departmentId: chosenDepartment.department_id, nurseId: nurse.id });
		showSnackbar(t("employee.status.cleared"), "success");
	}, [returnToActiveNurse, clearEmployeeStatus, chosenDepartment.department_id, showSnackbar]);

	const handleDeactivateClick = useCallback(() => {
		if (selectedNurse) {
			handleMenuClose();
			if (isNurseActiveInDepartment(selectedNurse, chosenDepartment.department_id)) {
				handleDeactivate(selectedNurse);
			} else {
				handleActivate(selectedNurse);
			}
		}
	}, [selectedNurse, handleMenuClose, handleDeactivate, handleActivate, chosenDepartment.department_id]);

	const { deactivateNursesCheckboxes } = useNurseFeatureData(deactivateDialogNurse ?? undefined);

	const handleDeactivateConfirm = useCallback(
		async (checkedState?: DialogCheckedState[]) => {
			if (!deactivateDialogNurse) return;
			const postShiftsCheckbox = checkedState?.find((checkbox) => checkbox.id === DEACTIVATE_POST_SHIFTS_CHECKBOX_ID);

			trackEvent("team_nurse_deactivate", {
				nurseId: deactivateDialogNurse.id,
				nurseName: formatPersonFullName(deactivateDialogNurse, false),
			});

			await scheduleDeactivation({
				departmentId: chosenDepartment.department_id,
				nurseId: deactivateDialogNurse.id,
				deactivationDate: "",
				postShifts: postShiftsCheckbox?.checked ?? false,
			});

			await refreshNursesData(chosenDepartment.department_id);
			setDeactivateDialogNurse(null);
			showSnackbar("Nurse deactivated successfully", "success");
		},
		[deactivateDialogNurse, scheduleDeactivation, chosenDepartment.department_id, refreshNursesData, showSnackbar],
	);

	const handleActivateConfirm = useCallback(async () => {
		if (!activateDialogNurse) return;

		trackEvent("team_nurse_activate", {
			nurseId: activateDialogNurse.id,
			nurseName: formatPersonFullName(activateDialogNurse, false),
		});

		await activateNurse({
			departmentId: chosenDepartment.department_id,
			nurseId: activateDialogNurse.id,
		});

		await refreshNursesData(chosenDepartment.department_id);
		setActivateDialogNurse(null);
		showSnackbar("Nurse activated successfully", "success");
	}, [activateDialogNurse, activateNurse, chosenDepartment.department_id, refreshNursesData, showSnackbar]);

	const handleSavePto = useCallback(
		(newDayOff: DayOff) => {
			if (!addPtoNurseId) return;
			const nurse = nurses.find((n) => n.id === addPtoNurseId);
			if (!nurse) return;

			updateNurse({
				...nurse,
				employeeSpecificPreferences: [...nurse.employeeSpecificPreferences, ...daysOffAndPtoToNursePreferences([newDayOff], nurse)],
			});

			trackEvent("team_pto_added", {
				nurse_id: nurse.id,
				date_from: newDayOff.dateFrom?.toFormat("yyyy-MM-dd"),
				date_to: newDayOff.dateTo?.toFormat("yyyy-MM-dd"),
			});

			setAddPtoNurseId(null);
			showSnackbar("PTO added successfully", "success");
		},
		[addPtoNurseId, nurses, updateNurse, showSnackbar],
	);

	const handleRowClick = useCallback(
		(nurse: Nurse) => {
			track("team_nurse_clicked", {
				nurse_id: nurse.id,
				nurse_name: formatPersonFullName(nurse, false),
				nurse_role: nurse.role,
				employment_type: nurse.employmentType,
			});
			navigate(`/${chosenDepartment.department_id}/nurse/${nurse.id}`, {
				state: { navigationSrc: window.location.pathname },
			});
		},
		[chosenDepartment.department_id, navigate, track],
	);

	/**
	 * Enter opens the focused row's profile. MUI publishes `onRowClick` only from its mouse handler,
	 * so without this there is no keyboard route to a profile at all.
	 *
	 * The division of labour is Enter = navigate, Space / Shift+Space = select (both already MUI's).
	 * Everything guarded below is a key MUI or a nested control already owns: Ctrl+Enter opens the
	 * column menu, the checkbox cell is the selection affordance, and rows carry their own action
	 * button plus the HRIS decision badge — Enter on one of those must activate it, not navigate away
	 * from it.
	 */
	const handleCellKeyDown = useCallback(
		(params: GridCellParams<Nurse>, event: KeyboardEvent<HTMLElement>) => {
			if (event.key !== "Enter" || event.defaultPrevented) return;
			if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
			if (params.field === "__check__") return;
			// Never navigate out from under a control the user was actually activating — the row-action
			// menu button and the HRIS decision badge both live inside cells. `instanceof` rather than a
			// cast: `.closest` would throw on a non-Element target, so the old `as HTMLElement | null`
			// plus `?.` described a null case that cannot happen and swallowed one that can.
			// NB: not `[tabindex]` — MUI's roving focus puts a tabindex on the focused CELL, so that
			// selector matches every target and Enter would never navigate at all.
			if (event.target instanceof Element && event.target.closest("button, a, input, [role='menuitem'], [role='button']") !== null) return;
			event.preventDefault();
			handleRowClick(params.row);
		},
		[handleRowClick],
	);

	const { columns, sortingOptions, defaultSortState } = useTeamGridColumns({
		departmentId: chosenDepartment.department_id,
		onMenuClick: handleMenuClick,
		decisionsByNurseId,
		onDecisionClick: handleDecisionClick,
	});

	return (
		<>
			<Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}>
				<SystemDataGrid<Nurse>
					rows={nurses}
					columns={columns}
					getRowId={(n) => n.id}
					sortingOptions={sortingOptions}
					defaultSortState={defaultSortState}
					localStorageKey={teamTableSortingKey}
					columnPicker={{ defaultVisibility: TEAM_DEFAULT_COLUMN_VISIBILITY }}
					dataTestId="team-table"
					leadingToolbarNodes={filtersNode}
					toolbarSxOverride={{ gap: 1.5, rowGap: 1 }}
					// The sort is stated in words underneath, since superscript numerals in the headers are
					// not something anyone reads.
					showSortSummary
					// Search sits directly on top of the table, divider not gap, so the two read as one surface.
					flushToolbar
					canFilterByColumn={canFilterByColumn}
					onFilterByColumn={onFilterByColumn}
					filterByHint={filterByHint}
					// Selection is always available rather than a mode reached through the header menu.
					hoverCheckboxSelection
					// WCAG 2.4.7 (focus visible): the app-wide default suppresses the cell focus ring, which
					// leaves a keyboard user with no idea which row they are on. Opted back in here rather
					// than flipped globally, which would restyle every grid in the app.
					showBorderOnCellFocus
					loading={loading}
					// Enough to fill a laptop viewport, so the shell doesn't visibly grow when rows land.
					skeletonRowCount={12}
					dataGridProps={{
						onRowClick: ({ row }) => handleRowClick(row),
						onCellKeyDown: handleCellKeyDown,
						// `RELAXED`, not the `COMPACT` this shipped with: these rows carry a name badge and status
						// chips, and a row whose tallest content is a chip needs more than the 36px meant for
						// scanning plain text — 44px still had the chips touching the row's edges.
						rowHeight: GRID_ROW_HEIGHT.RELAXED,
						checkboxSelection: true,
						// Clicking a row navigates to the profile, so the checkbox must be the only thing that
						// selects — otherwise every attempt to open someone would tick them instead.
						disableRowSelectionOnClick: true,
						// Keeps the selection model in `include` mode always. Left off, header select-all and
						// Cmd/Ctrl+A emit `{type: "exclude", ids: ∅}` ("everything except nothing"), which the
						// page then has to invert against its own filtered rows to know who is selected.
						disableRowSelectionExcludeModel: true,
						rowSelectionModel,
						onRowSelectionModelChange,

						// used to apply the "team-row-inactive" class for inactive nurses.
						getRowClassName: ({ row }) => (isNurseActiveInDepartment(row, chosenDepartment.department_id) ? "" : "team-row-inactive"),

						...(emptyState ? { slots: { noRowsOverlay: () => <>{emptyState}</>, noResultsOverlay: () => <>{emptyState}</> } } : {}),
					}}
					sx={{
						// The page frames the rail and this grid together, so the grid must not draw a second
						// border inside that frame.
						border: "none",
						borderRadius: 0,
						// The header row needs a hard edge under it. Without one the tinted header and the first
						// data row bleed into each other and the table loses its top boundary — the mock's header
						// carries a heavier rule than the row lines for exactly this reason.
						"& .MuiDataGrid-columnHeaders": { borderBottom: `1px solid ${NEUTRAL[300]}` },
						"& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaders .MuiDataGrid-filler": {
							borderBottom: `1px solid ${NEUTRAL[300]}`,
						},
						// Column separators, so the columns read as columns rather than as free-floating text.
						"& .MuiDataGrid-columnSeparator": { opacity: 1, color: NEUTRAL[300] },
						"& .MuiDataGrid-row": { cursor: "pointer" },
						"& .MuiDataGrid-row:hover": { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08) },
						// The row menu costs no width at rest and appears where the pointer already is.
						[`& .${TEAM_ROW_ACTION_CLASS}`]: {
							opacity: 0,
							transition: `opacity ${MOTION.DURATION.HOVER}ms ${MOTION.EASE_OUT}`,
						},
						[`& .MuiDataGrid-row:hover .${TEAM_ROW_ACTION_CLASS}, & .${TEAM_ROW_ACTION_CLASS}:focus-visible`]: { opacity: 1 },
						"@media (prefers-reduced-motion: reduce)": {
							[`& .${TEAM_ROW_ACTION_CLASS}`]: { transition: "none" },
						},
						"& .MuiDataGrid-row.team-row-inactive": {
							opacity: 0.6,
							backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
						},
						// Demo-only: plain light-gray column header (instead of the tinted default).
						...(isRealHospitalDemo && {
							"& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
								backgroundColor: NEUTRAL[100],
							},
						}),
					}}
				/>
			</Box>

			<Menu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={handleMenuClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				{userInfo?.support && (
					<MenuItem
						onClick={() => {
							if (selectedNurse) {
								setSetPasswordNurse(selectedNurse);
								handleMenuClose();
							}
						}}
						disabled={!selectedNurse?.userId}
					>
						{t("set.password")}
					</MenuItem>
				)}
				{!selectedNurse?.userId && (
					<MenuItem onClick={handleCreateUserClick} disabled={isResetPasswordLoading}>
						Create User
					</MenuItem>
				)}
				<MenuItem onClick={handleResetPasswordClick} disabled={!selectedNurse?.userId || isResetPasswordLoading}>
					{t("reset.password")}
				</MenuItem>
				<MenuItem onClick={handleAddPtoClick}>{t("team.menu.add_pto")}</MenuItem>
				<MenuItem onClick={handleEditStatusClick} data-testid="team-menu-edit-status">
					{t("employee.status.edit.action")}
				</MenuItem>
				{selectedNurse && isNurseOnLeaveOrSuspended(selectedNurse, chosenDepartment.department_id) && (
					<MenuItem onClick={handleReturnToActiveClick} data-testid="team-menu-return-to-active">
						{t("employee.status.return.to.active")}
					</MenuItem>
				)}
				<MenuItemWithReason
					disabledReason={
						selectedNurse && selectedNurse.departments.length > 1 && selectedNurse.homeDepartmentId !== chosenDepartment.department_id
							? t("deactivation.only.home.facility")
							: undefined
					}
					onClick={handleDeactivateClick}
					dataTestId="team-menu-deactivate"
				>
					{selectedNurse && isNurseActiveInDepartment(selectedNurse, chosenDepartment.department_id) ? t("team.menu.deactivate") : t("team.menu.activate")}
				</MenuItemWithReason>
			</Menu>

			{setPasswordNurse?.userId && (
				<SetPasswordDialog
					open={!!setPasswordNurse}
					onClose={() => setSetPasswordNurse(null)}
					userId={setPasswordNurse.userId}
					userName={formatPersonName(setPasswordNurse)}
				/>
			)}

			{addPtoNurseId && <AddDayOffDialog open={!!addPtoNurseId} onClose={() => setAddPtoNurseId(null)} onSave={handleSavePto} nurseId={addPtoNurseId} />}

			{editStatusNurse && (
				<EditEmployeeStatusDialog
					open={!!editStatusNurse}
					nurse={editStatusNurse}
					departmentId={chosenDepartment.department_id}
					onClose={() => setEditStatusNurse(null)}
					onSaved={() => showSnackbar(t("employee.status.saved"), "success")}
				/>
			)}

			{returnToActiveNurse && (
				<SystemDialog
					open={!!returnToActiveNurse}
					onClose={() => setReturnToActiveNurse(null)}
					title={t("employee.status.return.to.active")}
					body={t("employee.status.return.confirm", { name: formatPersonName(returnToActiveNurse) })}
					onConfirm={handleReturnToActiveConfirm}
					cancel={t("cancel")}
					confirm={t("employee.status.return.to.active")}
					confirmLoading={isClearingEmployeeStatus}
					dataTestId="team-return-to-active"
				/>
			)}

			{deactivateDialogNurse && (
				<SystemDialog
					open={!!deactivateDialogNurse}
					onClose={() => setDeactivateDialogNurse(null)}
					title="Deactivate Nurse"
					body={<div>Are you sure you want to deactivate {formatPersonName(deactivateDialogNurse)}?</div>}
					onConfirm={handleDeactivateConfirm}
					cancel="Cancel"
					confirm="Deactivate Nurse"
					checkboxes={deactivateNursesCheckboxes}
				/>
			)}

			{activateDialogNurse && (
				<SystemDialog
					open={!!activateDialogNurse}
					onClose={() => setActivateDialogNurse(null)}
					title="Activate Nurse"
					body={<div>Are you sure you want to activate {formatPersonName(activateDialogNurse)}?</div>}
					onConfirm={handleActivateConfirm}
					cancel="Cancel"
					confirm="Activate Nurse"
				/>
			)}

			<StatusDecisionDialog
				decision={decisionDialog}
				departmentId={chosenDepartment.department_id}
				nurseName={decisionDialogNurseName}
				onClose={() => setDecisionDialog(null)}
			/>
		</>
	);
};
